<template>
  <div class="asignaciones-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="asignaciones-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-icon-box">
          <v-icon color="#F2F2ED" size="20">mdi-clipboard-check-outline</v-icon>
        </div>
        <div>
          <h1 class="page-title">Rutinas asignadas</h1>
          <p class="page-sub">Gestiona el estado y progreso de las rutinas de tus miembros</p>
        </div>
        <button class="btn-ghost header-refresh" @click="cargarAsignaciones">
          <v-icon size="15" class="mr-2">mdi-refresh</v-icon>
          Actualizar
        </button>
      </header>

      <v-progress-linear :active="cargando" :indeterminate="cargando" absolute top color="#C8FF4D"></v-progress-linear>

      <!-- Filtros -->
      <section class="fade-in delay-100">
        <div class="card-flat">
          <div class="card-flat-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Filtros de búsqueda
            </span>
          </div>
          <div class="card-flat-body">
            <div class="field-row field-row-3">
              <div class="field">
                <label class="field-label">Miembro</label>
                <v-autocomplete v-model="filtros.miembro_id" :items="miembros" item-text="text" item-value="id"
                  clearable outlined dense hide-details="auto" placeholder="Todos los miembros"
                  class="dark-input"></v-autocomplete>
              </div>
              <div class="field">
                <label class="field-label">Estado</label>
                <v-select v-model="filtros.estado" :items="estados" clearable outlined dense hide-details="auto"
                  placeholder="Todos los estados" class="dark-input"></v-select>
              </div>
              <div class="field">
                <label class="field-label">Fecha</label>
                <v-menu v-model="menuFecha" :close-on-content-click="false" transition="scale-transition" offset-y
                  min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="filtros.fecha" readonly clearable outlined dense hide-details="auto"
                      prepend-inner-icon="mdi-calendar" placeholder="Cualquier fecha" v-bind="attrs" v-on="on"
                      class="dark-input"></v-text-field>
                  </template>
                  <v-date-picker v-model="filtros.fecha" @input="menuFecha = false" class="dark-picker"></v-date-picker>
                </v-menu>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Lista de asignaciones -->
      <section class="fade-in delay-200 mt-lg">
        <div class="card-flat card-flat-nopad">
          <div class="table-head">
            <span class="section-eyebrow">
              <span class="tick-mark"></span>
              Lista de rutinas asignadas
            </span>
            <div class="search-input-wrapper">
              <v-icon size="16" color="#5C5C62">mdi-magnify</v-icon>
              <input v-model="search" type="text" class="search-input" placeholder="Buscar…" />
            </div>
          </div>

          <v-data-table :headers="headers" :items="asignacionesFiltradas" :search="search" :loading="cargando"
            loading-text="Cargando asignaciones…" no-data-text="No hay rutinas asignadas con los filtros seleccionados"
            class="dash-table" :footer-props="{ itemsPerPageText: 'Por página' }">
            <template v-slot:[`item.miembro_nombre`]="{ item }">
              <span class="cell-strong">{{ item.miembro_nombre }}</span>
            </template>

            <template v-slot:[`item.rutina_nombre`]="{ item }">
              <span class="cell-secondary">{{ item.rutina_nombre }}</span>
            </template>

            <template v-slot:[`item.usuario_nombre`]="{ item }">
              <span class="cell-secondary">{{ item.usuario_nombre }}</span>
            </template>

            <template v-slot:[`item.fecha_inicio`]="{ item }">
              <span class="cell-mono">{{ fechaFormateada(item.fecha_inicio) }}</span>
            </template>

            <template v-slot:[`item.fecha_fin`]="{ item }">
              <span class="cell-mono">{{ fechaFormateada(item.fecha_fin) }}</span>
            </template>

            <template v-slot:[`item.activa`]="{ item }">
              <span class="status-tag" :class="item.activa ? 'status-tag-success' : 'status-tag-error'">
                {{ item.activa ? 'Activa' : 'Inactiva' }}
              </span>
            </template>

            <template v-slot:[`item.progreso`]="{ item }">
              <div class="progress-cell">
                <div class="progress-track">
                  <div class="progress-fill" :class="claseProgreso(item)"
                    :style="{ width: calcularProgreso(item) + '%' }"></div>
                </div>
                <span class="progress-text">{{ calcularProgreso(item) }}%</span>
              </div>
            </template>

            <template v-slot:[`item.acciones`]="{ item }">
              <div class="flex gap-xs">
                <button class="icon-btn" @click="verDetalles(item)" title="Ver detalles">
                  <v-icon size="15" color="#8C8C93">mdi-eye</v-icon>
                </button>
                <button class="icon-btn" @click="descargarPDF(item)" title="Descargar PDF">
                  <v-icon size="15" color="#8C8C93">mdi-download</v-icon>
                </button>
                <button class="icon-btn" :class="item.activa ? 'icon-btn-danger' : 'icon-btn-accent'"
                  @click="toggleEstado(item)" :title="item.activa ? 'Pausar' : 'Reactivar'">
                  <v-icon size="15" :color="item.activa ? '#FF6B6B' : '#C8FF4D'">
                    {{ item.activa ? 'mdi-pause' : 'mdi-play' }}
                  </v-icon>
                </button>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="empty-state">
                <div class="empty-icon">
                  <v-icon size="28" color="#5C5C62">mdi-clipboard-off-outline</v-icon>
                </div>
                <h3 class="empty-title">No hay rutinas asignadas</h3>
                <p class="empty-desc">No se encontraron resultados con los filtros seleccionados</p>
              </div>
            </template>
          </v-data-table>
        </div>
      </section>
    </div>

    <!-- Diálogo de detalles -->
    <v-dialog v-model="dialogoDetalles" max-width="600px">
      <div class="modal-panel" v-if="asignacionSeleccionada">
        <div class="modal-head detail-head">
          <h3 class="modal-title">Detalles de asignación</h3>
          <span class="status-tag" :class="asignacionSeleccionada.activa ? 'status-tag-success' : 'status-tag-error'">
            {{ asignacionSeleccionada.activa ? 'Activa' : 'Inactiva' }}
          </span>
        </div>

        <div class="modal-body">
          <div class="info-table">
            <div class="info-row">
              <span class="info-key">Miembro</span>
              <span class="info-val">{{ asignacionSeleccionada.miembro_nombre }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">Rutina</span>
              <span class="info-val">{{ asignacionSeleccionada.rutina_nombre }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">Asignada por</span>
              <span class="info-val">{{ asignacionSeleccionada.usuario_nombre }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">Fecha inicio</span>
              <span class="info-val">{{ fechaFormateada(asignacionSeleccionada.fecha_inicio) }}</span>
            </div>
            <div class="info-row">
              <span class="info-key">Fecha fin</span>
              <span class="info-val">{{ fechaFormateada(asignacionSeleccionada.fecha_fin) }}</span>
            </div>
            <div class="info-row" v-if="asignacionSeleccionada.notas">
              <span class="info-key">Notas</span>
              <span class="info-val">{{ asignacionSeleccionada.notas }}</span>
            </div>
          </div>

          <div class="progress-block">
            <div class="progress-labels">
              <span class="mini-eyebrow">Progreso</span>
              <span class="progress-pct">{{ calcularProgreso(asignacionSeleccionada) }}%</span>
            </div>
            <div class="progress-track">
              <div class="progress-fill" :class="claseProgreso(asignacionSeleccionada)"
                :style="{ width: calcularProgreso(asignacionSeleccionada) + '%' }"></div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="dialogoDetalles = false">Cerrar</button>
          <button class="btn-primary" @click="descargarPDF(asignacionSeleccionada)">
            <v-icon size="15" class="mr-2">mdi-download</v-icon>
            Descargar PDF
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import jsPDF from "jspdf";
import 'jspdf-autotable';

export default {
  name: "GestionAsignaciones",
  data: () => ({
    cargando: false,
    search: '',
    menuFecha: false,
    dialogoDetalles: false,
    asignaciones: [],
    asignacionSeleccionada: null,
    miembros: [],
    mostrarMensaje: false,
    mensaje: { texto: '', color: '' },
    filtros: {
      miembro_id: null,
      estado: null,
      fecha: null
    },
    estados: [
      { text: 'Activas', value: 'activa' },
      { text: 'Inactivas', value: 'inactiva' }
    ],
    headers: [
      { text: 'Miembro', value: 'miembro_nombre' },
      { text: 'Rutina', value: 'rutina_nombre' },
      { text: 'Asignada por', value: 'usuario_nombre' },
      { text: 'Fecha Inicio', value: 'fecha_inicio' },
      { text: 'Fecha Fin', value: 'fecha_fin' },
      { text: 'Estado', value: 'activa' },
      { text: 'Progreso', value: 'progreso', sortable: false },
      { text: 'Acciones', value: 'acciones', sortable: false, width: '140px' }
    ]
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle'
        case 'error': return 'mdi-alert-circle'
        default: return 'mdi-information'
      }
    },

    asignacionesFiltradas() {
      let filtered = this.asignaciones;

      // Filtrar por miembro
      if (this.filtros.miembro_id) {
        filtered = filtered.filter(a => a.miembro_id == this.filtros.miembro_id);
      }

      // Filtrar por estado
      if (this.filtros.estado) {
        filtered = filtered.filter(a =>
          this.filtros.estado === 'activa' ? a.activa : !a.activa
        );
      }

      // Filtrar por fecha
      if (this.filtros.fecha) {
        filtered = filtered.filter(a => a.fecha_inicio === this.filtros.fecha);
      }

      return filtered;
    }
  },

  mounted() {
    this.cargarAsignaciones();
    this.cargarMiembros();
  },

  methods: {
    fechaFormateada(fecha) {
      // fecha_inicio / fecha_fin son columnas DATE (sin hora), a diferencia
      // de created_at/updated_at que sí son DATETIME. formatearFechaHora()
      // espera un espacio + hora en el string, así que aquí formateamos
      // la fecha directamente sin depender de esa utilidad.
      if (!fecha) return 'Sin fecha';
      const fechaObj = new Date(fecha + 'T00:00:00');
      if (isNaN(fechaObj.getTime())) return 'Fecha inválida';
      return fechaObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    async cargarAsignaciones() {
      this.cargando = true;
      try {
        // El metodo debe coincidir con el case del switch en el router PHP
        // (miembro_rutina.php), no con el nombre de la función interna.
        const respuesta = await HttpService.obtenerConDatos(
          { metodo: "get_todas" },
          "miembro_rutina.php"
        );
        this.asignaciones = respuesta;
      } catch (error) {
        this.mostrarError('Error cargando asignaciones');
      }
      this.cargando = false;
    },

    async cargarMiembros() {
      try {
        const respuesta = await HttpService.obtenerConDatos(
          { metodo: "obtener_miembros_disponibles" },
          "miembros.php"
        );
        this.miembros = respuesta.map(m => ({
          id: m.id,
          text: `Membresia: ${m.matricula} - ${m.nombre}`
        }));
      } catch (error) {
        console.error("Error cargando miembros:", error);
      }
    },

    verDetalles(asignacion) {
      this.asignacionSeleccionada = asignacion;
      this.dialogoDetalles = true;
    },

    // Único método toggleEstado (antes estaba duplicado: la segunda
    // definición pisaba a la primera). El metodo enviado debe coincidir
    // con el case del router PHP ("desactivar" / "activar"), no con el
    // nombre de la función interna (desactivarRutina/activarRutina).
    async toggleEstado(asignacion) {
      try {
        const metodo = asignacion.activa ? "desactivar" : "activar";
        const payload = { metodo, id: asignacion.id };

        const resultado = await HttpService.registrar(payload, "miembro_rutina.php");

        if (resultado.success) {
          asignacion.activa = !asignacion.activa;
          this.mostrarMensaje = true;
          this.mensaje = {
            texto: asignacion.activa ? 'Rutina reactivada correctamente' : 'Rutina pausada correctamente',
            color: 'success'
          };
        } else {
          this.mostrarError(resultado.message);
        }
      } catch (error) {
        this.mostrarError('Error cambiando estado');
      }
    },

    async descargarPDF(asignacion) {
      try {
        const rutina = await HttpService.obtenerConDatos(
          { metodo: "get_by_id", id: asignacion.rutina_id },
          "rutinas.php"
        );

        let nombreMiembro = asignacion.miembro_nombre;
        if (!nombreMiembro) {
          const miembro = await HttpService.obtenerConDatos(
            { metodo: "obtener_id", id: asignacion.miembro_id },
            "miembros.php"
          );
          nombreMiembro = `${miembro.nombre} ${miembro.apellido}`;
        }

        const doc = new jsPDF();

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        const contentWidth = pageWidth - (margin * 2);

        if (!asignacion.activa) {
          doc.setFontSize(60);
          doc.setTextColor(200, 0, 0);
          doc.setFont(undefined, 'bold');
          doc.text("INACTIVA", pageWidth / 2, 150, { align: 'center', angle: 45 });
        }

        doc.setFillColor(20, 20, 22);
        doc.rect(0, 0, pageWidth, 60, 'F');

        doc.setFontSize(24);
        doc.setTextColor(200, 255, 77);
        doc.setFont(undefined, 'bold');
        doc.text("Sport Fortius World", pageWidth / 2, 25, { align: 'center' });

        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.text("RUTINA DE ENTRENAMIENTO", pageWidth / 2, 40, { align: 'center' });

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');

        let y = 80;

        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');
        doc.roundedRect(margin, y, contentWidth, 50, 3, 3, 'S');

        doc.setFont(undefined, 'bold');
        doc.text("INFORMACIÓN DE LA RUTINA", margin + 10, y + 10);
        doc.setFont(undefined, 'normal');

        const col1 = margin + 10;
        const col2 = pageWidth / 2;

        doc.text(`Miembro: ${nombreMiembro}`, col1, y + 20);
        doc.text(`Rutina: ${rutina.nombre}`, col1, y + 30);
        doc.text(`Objetivo: ${this.formatObjetivo(rutina.objetivo)}`, col1, y + 40);

        doc.text(`Inicio: ${this.formatFechaPDF(asignacion.fecha_inicio)}`, col2, y + 20);
        doc.text(`Fin: ${this.formatFechaPDF(asignacion.fecha_fin)}`, col2, y + 30);
        doc.text(`Duración: ${rutina.duracion_semanas} semanas`, col2, y + 40);

        y += 60;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(16);
        doc.setTextColor(20, 20, 22);
        doc.text("PLAN DE ENTRENAMIENTO", margin, y);
        y += 10;

        rutina.dias.forEach((dia, diaIndex) => {
          if (y > 240) {
            doc.addPage();
            y = 20;

            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Rutina: ${rutina.nombre} - Página ${doc.internal.getNumberOfPages()}`, margin, 10);
            doc.text(`Miembro: ${nombreMiembro}`, pageWidth - margin, 10, { align: 'right' });
            doc.setTextColor(0, 0, 0);
            y = 30;
          }

          doc.setFillColor(30, 30, 33);
          doc.setDrawColor(20, 20, 22);
          doc.roundedRect(margin, y, contentWidth, 15, 3, 3, 'F');

          doc.setFontSize(14);
          doc.setTextColor(200, 255, 77);
          doc.setFont(undefined, 'bold');
          doc.text(`DÍA ${diaIndex + 1}: ${dia.nombre_dia.toUpperCase()}`, margin + 10, y + 10);
          y += 20;

          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'normal');

          dia.ejercicios.forEach((ejercicio, ejIndex) => {
            if (y > 250) {
              doc.addPage();
              y = 20;

              doc.setFontSize(10);
              doc.setTextColor(100, 100, 100);
              doc.text(`Rutina: ${rutina.nombre} - Página ${doc.internal.getNumberOfPages()}`, margin, 10);
              doc.text(`Miembro: ${nombreMiembro}`, pageWidth - margin, 10, { align: 'right' });
              doc.setTextColor(0, 0, 0);
              y = 30;
            }

            if (ejIndex % 2 === 0) {
              doc.setFillColor(245, 245, 245);
              doc.rect(margin, y, contentWidth, 35, 'F');
            }

            doc.setFont(undefined, 'bold');
            doc.text(`${ejIndex + 1}. ${ejercicio.ejercicio_nombre}`, margin + 5, y + 8);

            doc.setFont(undefined, 'normal');
            doc.setFontSize(11);

            const detailY = y + 16;
            const detailCol1 = margin + 15;
            const detailCol2 = detailCol1 + 50;
            const detailCol3 = detailCol2 + 45;

            doc.text(`Series: ${ejercicio.series}`, detailCol1, detailY);
            doc.text(`Reps: ${ejercicio.repeticiones}`, detailCol2, detailY);
            doc.text(`Descanso: ${ejercicio.descanso_segundos}s`, detailCol3, detailY);

            if (ejercicio.peso_recomendado) {
              doc.text(`Peso: ${ejercicio.peso_recomendado}`, detailCol3 + 50, detailY);
            }

            y += 25;

            if (ejIndex < dia.ejercicios.length - 1) {
              doc.setDrawColor(200, 200, 200);
              doc.line(margin + 10, y - 2, margin + contentWidth - 10, y - 2);
              y += 5;
            }
          });

          y += 15;
        });

        if (asignacion.notas && y < 250) {
          if (y > 220) {
            doc.addPage();
            y = 20;
          }

          doc.setFontSize(14);
          doc.setTextColor(20, 20, 22);
          doc.setFont(undefined, 'bold');
          doc.text("NOTAS DEL ENTRENADOR", margin, y);
          y += 10;

          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'normal');

          const splitNotes = doc.splitTextToSize(asignacion.notas, contentWidth - 10);
          doc.text(splitNotes, margin + 5, y + 5);
          y += (splitNotes.length * 7) + 10;
        }

        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);

          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, 285, { align: 'center' });

          doc.text(`Asignada por: ${asignacion.usuario_nombre}`, margin, 285);
          doc.text(`Fecha: ${this.formatFechaPDF(asignacion.created_at)}`, pageWidth - margin, 285, { align: 'right' });
        }

        const fileName = `Rutina_${nombreMiembro.replace(/\s+/g, '_')}_${rutina.nombre.replace(/\s+/g, '_')}.pdf`;

        if (navigator.msSaveBlob) {
          const blob = doc.output('blob');
          navigator.msSaveBlob(blob, fileName);
        } else {
          doc.save(fileName);
        }

      } catch (error) {
        console.error("Error generando PDF:", error);
        this.mostrarError('Error generando PDF: ' + error.message);
      }
    },

    calcularProgreso(asignacion) {
      const inicio = new Date(asignacion.fecha_inicio);
      const fin = new Date(asignacion.fecha_fin);
      const hoy = new Date();

      const totalDias = (fin - inicio) / (1000 * 60 * 60 * 24);
      const diasTranscurridos = (hoy - inicio) / (1000 * 60 * 60 * 24);

      return Math.min(100, Math.max(0, Math.round((diasTranscurridos / totalDias) * 100)));
    },

    claseProgreso(asignacion) {
      const progreso = this.calcularProgreso(asignacion);
      if (progreso < 30) return 'progress-danger';
      if (progreso < 70) return 'progress-warn';
      return 'progress-ok';
    },

    formatFechaPDF(fecha) {
      if (!fecha) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(fecha).toLocaleDateString('es-ES', options);
    },

    formatObjetivo(objetivo) {
      const objetivos = {
        'perdida_grasa': 'Pérdida de grasa',
        'ganancia_muscular': 'Ganancia muscular',
        'fuerza': 'Fuerza',
        'resistencia': 'Resistencia',
        'general': 'General'
      };
      return objetivos[objetivo] || objetivo;
    },

    mostrarError(mensaje) {
      this.mostrarMensaje = true;
      this.mensaje = {
        texto: mensaje,
        color: 'error'
      };
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.asignaciones-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.asignaciones-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.asignaciones-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 20px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.header-refresh {
  margin-left: auto;
}

/* ===== CARD ===== */
.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.card-flat-nopad {
  overflow: hidden;
  position: relative;
}

.card-flat-head,
.table-head {
  padding: 18px 22px;
  border-bottom: 1px solid var(--card-border);
}

.table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.card-flat-body {
  padding: 22px;
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

/* ===== FIELDS ===== */
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field-row-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 12px;
  max-width: 220px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 8px 0;
  color: var(--text-primary);
  font-size: 12.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

/* ===== TABLE CELLS ===== */
.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.cell-mono {
  font-family: var(--font-mono);
  font-size: 11.5px;
  color: var(--text-tertiary);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
  white-space: nowrap;
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.status-tag-error {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.progress-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 110px;
}

.progress-track {
  flex: 1;
  height: 5px;
  background: var(--card-border);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
}

.progress-fill.progress-ok {
  background: var(--accent);
}

.progress-fill.progress-warn {
  background: var(--warning);
}

.progress-fill.progress-danger {
  background: var(--error);
}

.progress-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  min-width: 32px;
  text-align: right;
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-accent:hover {
  border-color: rgba(200, 255, 77, 0.3);
  background: var(--accent-dim);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== EMPTY ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.info-table {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
}

.info-key {
  font-size: 12px;
  color: var(--text-tertiary);
  flex-shrink: 0;
}

.info-val {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;
}

.progress-block {
  margin-top: 8px;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.mini-eyebrow {
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.progress-pct {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-primary);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

/* ===== Overrides Vuetify ===== */
.dark-input >>> .v-input__slot {
  background: var(--bg) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.dark-input >>> fieldset {
  border-color: var(--card-border) !important;
  border-width: 1px !important;
  border-radius: 4px !important;
}

.dark-input >>> .v-input--is-focused fieldset {
  border-color: var(--accent) !important;
  border-width: 1.5px !important;
}

.dark-input >>> input,
.dark-input >>> .v-select__selection {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13.5px !important;
}

.dark-input >>> .v-icon {
  color: var(--text-tertiary) !important;
}

.asignaciones-page >>> .v-menu__content {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 4px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}

.asignaciones-page >>> .v-list {
  background: transparent !important;
  padding: 4px !important;
}

.asignaciones-page >>> .v-list-item {
  border-radius: 4px !important;
}

.asignaciones-page >>> .v-list-item__title {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 13px !important;
}

.asignaciones-page >>> .v-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.asignaciones-page >>> .v-list-item--active {
  background: var(--accent-dim) !important;
}

.dark-picker {
  background: var(--card) !important;
}

.dark-picker >>> .v-date-picker-header,
.dark-picker >>> .v-picker__body,
.dark-picker >>> .v-date-picker-table {
  background: var(--card) !important;
}

.dark-picker >>> .v-date-picker-header button,
.dark-picker >>> .v-date-picker-header .v-icon,
.dark-picker >>> .v-date-picker-table th {
  color: var(--text-primary) !important;
}

.dark-picker >>> .v-date-picker-table .v-btn {
  color: var(--text-secondary) !important;
}

.dark-picker >>> .v-date-picker-table .v-btn.v-btn--active {
  background: var(--accent) !important;
  color: var(--bg) !important;
}

/* ===== Overrides v-data-table ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 22px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 22px !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
  font-size: 12px;
  padding: 0 22px;
}

.dash-table >>> .v-data-footer__select .v-select__selection {
  color: var(--text-secondary) !important;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .asignaciones-page {
    padding: 20px;
  }

  .page-header {
    flex-wrap: wrap;
  }

  .header-refresh {
    margin-left: 0;
    width: 100%;
  }

  .field-row,
  .field-row-3 {
    grid-template-columns: 1fr;
  }

  .table-head {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input-wrapper {
    max-width: none;
  }

  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 10px 14px !important;
    font-size: 12px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Planes/GestionAsignaciones.vue